import { useDispatch, useSelector } from "react-redux";
import {
  matchPath,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { routePermissions } from "./route-permissions";
import useAuthority from "../hooks/useAuthority";
import { AUTHORITY_TYPE, authServerUrl, statusConst } from "../const";
import LoadingSpinner from "../components/LoadingSpinner";
import { useCallback, useEffect, useRef } from "react";

const AuthGuard = () => {
  const user = useSelector((state) => state.user);
  const location = useLocation();

  const permissionStatus = useSelector((state) => state.user.permissionStatus);

  const routeData = routePermissions.find(
    (route) => matchPath(route.linkName, location.pathname) !== null,
  );

  const needAuth = useSelector((state) => state.user.needAuth);

  const navigate = useNavigate();
  const popupRef = useRef(null);
  const timerRef = useRef(null);

  const handleLogout = useCallback(() => {
    popupRef.current = window.open(
      `${authServerUrl}/logout`,
      "logoutPopup",
      "width=500,height=600",
    );

    timerRef.current = window.setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        clearInterval(timerRef.current);
      }
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      popupRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (needAuth) {
      handleLogout();
      navigate("/login", { replace: true });
    }
  }, [needAuth, navigate, handleLogout]);

  const { view } = useAuthority({
    type: AUTHORITY_TYPE.FORM,
    module: routeData?.module,
    form: routeData?.form,
    field: "",
    editMode: false,
    authenticated: !!routeData,
  });

  if (
    permissionStatus === statusConst.IDLE ||
    permissionStatus === statusConst.PENDING
  ) {
    return <LoadingSpinner message="Setting things up" />;
  }

  if (
    (permissionStatus === statusConst.SUCCESS ||
      permissionStatus === statusConst.FAILURE) &&
    !user.authenticated
  ) {
    return <Navigate to="/login" />;
  }

  if (
    (permissionStatus === statusConst.SUCCESS ||
      permissionStatus === statusConst.FAILURE) &&
    !view
  ) {
    return <Navigate to="/unauthorized" />;
  }

  return <Outlet />;
};

export default AuthGuard;
